(() => {
  if (customElements.get('foldinglink-liquid-glass')) return;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const mapCache = new Map();

  const roundedRectSdf = (x, y, width, height, radius) => {
    const halfW = width * 0.5;
    const halfH = height * 0.5;
    const innerW = Math.max(0, halfW - radius);
    const innerH = Math.max(0, halfH - radius);
    const qx = Math.abs(x - halfW) - innerW;
    const qy = Math.abs(y - halfH) - innerH;
    const outside = Math.hypot(Math.max(qx, 0), Math.max(qy, 0));
    const inside = Math.min(Math.max(qx, qy), 0);
    return outside + inside - radius;
  };

  const createRasterMap = (width, height, radius, edge, softening) => {
    const textureScale = Math.min(0.72, 720 / Math.max(width, height));
    const mapWidth = Math.max(96, Math.round(width * textureScale));
    const mapHeight = Math.max(96, Math.round(height * textureScale));
    const cacheKey = `${mapWidth}x${mapHeight}:${width}x${height}:${radius}:${edge.toFixed(2)}:${softening}`;
    const cached = mapCache.get(cacheKey);
    if (cached) return cached;

    const canvas = document.createElement('canvas');
    canvas.width = mapWidth;
    canvas.height = mapHeight;
    const context = canvas.getContext('2d', { alpha: false });
    const image = context.createImageData(mapWidth, mapHeight);
    const data = image.data;
    const stepX = width / mapWidth;
    const stepY = height / mapHeight;
    const normalStep = Math.max(0.75, Math.min(stepX, stepY));
    const softnessExponent = 1.25 - (softening / 30) * 0.55;

    for (let y = 0; y < mapHeight; y += 1) {
      const py = (y + 0.5) * stepY;
      for (let x = 0; x < mapWidth; x += 1) {
        const px = (x + 0.5) * stepX;
        const sdf = roundedRectSdf(px, py, width, height, radius);
        const inward = Math.max(0, -sdf);
        const edgePosition = clamp(inward / Math.max(1, edge), 0, 1);
        const lensEnvelope = Math.pow(Math.sin(Math.PI * edgePosition), softnessExponent);
        const profile = sdf <= 0 && inward < edge
          ? Math.sin(Math.PI * 2 * edgePosition) * lensEnvelope
          : 0;
        const gx = roundedRectSdf(px + normalStep, py, width, height, radius)
          - roundedRectSdf(px - normalStep, py, width, height, radius);
        const gy = roundedRectSdf(px, py + normalStep, width, height, radius)
          - roundedRectSdf(px, py - normalStep, width, height, radius);
        const length = Math.hypot(gx, gy) || 1;
        const nx = gx / length;
        const ny = gy / length;
        const amplitude = 0.14 * profile;
        const offset = (y * mapWidth + x) * 4;

        data[offset] = Math.round(128 + 127 * nx * amplitude);
        data[offset + 1] = Math.round(128 + 127 * ny * amplitude);
        data[offset + 2] = 128;
        data[offset + 3] = 255;
      }
    }

    context.putImageData(image, 0, 0);
    const result = {
      url: canvas.toDataURL('image/png'),
      filterRes: `${mapWidth} ${mapHeight}`,
    };
    mapCache.set(cacheKey, result);
    if (mapCache.size > 12) mapCache.delete(mapCache.keys().next().value);
    return result;
  };

  class FoldingLinkLiquidGlass extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._quality = this.dataset.quality || 'balanced';
      this._map = this.querySelector('[data-glass-map]');
      this._filter = this.querySelector('[data-glass-filter]');
      this._channels = Array.from(this.querySelectorAll('[data-glass-channel]'));
      this._resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[entries.length - 1];
        this._pendingSize = entry?.contentRect
          ? { width: entry.contentRect.width, height: entry.contentRect.height }
          : null;
        this._scheduleUpdate();
      });
      this._resizeObserver.observe(this);
      this._scheduleUpdate();
    }

    disconnectedCallback() {
      this._resizeObserver?.disconnect();
      if (this._frame) cancelAnimationFrame(this._frame);
      this._connected = false;
    }

    _scheduleUpdate() {
      if (this._frame) return;
      this._frame = requestAnimationFrame(() => {
        this._frame = 0;
        this._updateSurface();
      });
    }

    _updateSurface() {
      const rect = this._pendingSize || this.getBoundingClientRect();
      this._pendingSize = null;
      if (!rect.width || !rect.height) return;

      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const radius = clamp(Number(this.dataset.radius || 24), 0, Math.min(width, height) / 2);
      const edgeRange = clamp(Number(this.dataset.edgeRange || 7), 1, 25);
      const edge = clamp(Math.min(width, height) * edgeRange / 100, 7, 30);
      const softening = clamp(Number(this.dataset.softening || 16), 0, 30);
      const intensity = clamp(Number(this.dataset.intensity || 300), 0, 300);
      const sizeKey = `${this._quality}:${width}x${height}:${radius}:${edgeRange}:${softening}:${intensity}`;
      if (sizeKey === this._sizeKey) return;

      const map = createRasterMap(width, height, radius, edge, softening);
      this._map.setAttribute('href', map.url);
      this._map.setAttributeNS('http://www.w3.org/1999/xlink', 'href', map.url);
      if (this._quality === 'balanced') {
        this._filter?.setAttribute('filterRes', map.filterRes);
      } else {
        this._filter?.removeAttribute('filterRes');
      }

      const scales = {
        balanced: -intensity,
        red: -intensity,
        green: -(intensity - 15),
        blue: -(intensity - 30),
      };
      this._channels.forEach((channel) => {
        channel.setAttribute('scale', String(scales[channel.dataset.glassChannel]));
      });
      this.classList.add('liquid-glass-surface--filter-active');
      this._sizeKey = sizeKey;
    }
  }

  customElements.define('foldinglink-liquid-glass', FoldingLinkLiquidGlass);
})();
